import React from 'react';

import {recordEvent} from './Analytics';
import {useTranslations} from './TranslationsContext';

export type LegendConfig = {
  id: string;
  display: string;
  makeIcon: (idx: number, numForecasts: number) => React.ReactElement;
  shortDisplayName?: string;
  group?: string;
  ignoreGroupSelection?: boolean;
  onlyShowIfExpanded?: boolean;
  // What the item draws: a line, or a filled band.
  legendIcon?: 'line' | 'rect';
};

type LegendProps = {
  configs: LegendConfig[];
  onChange: (ids: string[], nextValue: boolean) => void;
  isVisibleById: {[key: string]: boolean};
};

const ITEM_SIZE = 16;
const TAB_BORDER_RADIUS = 4;

export default function Legend({configs, onChange, isVisibleById}: LegendProps) {
  const {t} = useTranslations();
  const handleLegendItemClick = React.useCallback(
    (id: string, nextValue: boolean) => {
      onChange([id], nextValue);
      recordEvent('legend_item_clicked', {['id']: id});
    },
    [onChange]
  );

  const handleLegendGroupClick = React.useCallback(
    (ids: string[], nextValue: boolean) => {
      onChange(ids, nextValue);
      recordEvent('legend_group_clicked', {ids: ids.join(',')});
    },
    [onChange]
  );

  const [isExpanded, setIsExpanded] = React.useState(false);
  const legendItemsByGroup: {[group: string]: LegendConfig[]} = React.useMemo(() => {
    return configs.reduce(
      (acc, config) => {
        if (config.group) {
          acc[config.group] = [...(acc[config.group] || []), config];
        }
        return acc;
      },
      {} as {[group: string]: LegendConfig[]}
    );
  }, [configs]);

  // Check if there are any items that are only visible in the expanded legend (ignore ungrouped items)
  const hasExpandedOnlyLegendItems = configs.some((c) => c.onlyShowIfExpanded);

  const unExpandedLegendItems = React.useMemo(() => {
    return configs.filter((c) => !c.onlyShowIfExpanded);
  }, [configs]);
  return (
    <div className="text-xs bg-white transition-opacity justify-items-center pt-2 pb-4 z-10">
      <div className="flex flex-row flex-wrap gap-6 justify-center">
        {/* Collapsed: show non-hidden groups as flat list; Expanded: show only truly ungrouped here */}
        <div className="flex flex-row flex-wrap gap-2 justify-center items-start">
          {(isExpanded ? legendItemsByGroup.ungrouped || [] : unExpandedLegendItems).map(
            ({id, display, makeIcon}) => {
              const isVisible = isVisibleById[id];
              return (
                <div
                  key={id}
                  data-legend-id={id}
                  className={`group flex items-center mx-2 ${
                    !isVisible && 'opacity-50'
                  } cursor-pointer`}
                  onClick={() => {
                    handleLegendItemClick(id, !isVisible);
                  }}
                >
                  <div>
                    <input
                      type="checkbox"
                      className="absolute group-hover:opacity-100 opacity-0"
                      style={{width: ITEM_SIZE, height: ITEM_SIZE}}
                      readOnly
                      checked={isVisible}
                    />

                    <svg
                      width={ITEM_SIZE}
                      height={ITEM_SIZE}
                      style={{borderRadius: TAB_BORDER_RADIUS}}
                    >
                      {makeIcon(-1, 0)}
                    </svg>
                  </div>
                  <div className="ml-2 select-none">{display}</div>
                </div>
              );
            }
          )}
        </div>

        {/* Grouped sections (visible only when expanded) */}
        {isExpanded &&
          Object.entries(legendItemsByGroup).map(([group, items]) => {
            const allVisible = items.every((it) => it.ignoreGroupSelection || isVisibleById[it.id]);
            const someVisible = items.some((it) => isVisibleById[it.id]);
            return (
              <div key={group} className="flex flex-col gap-1">
                <div
                  className="flex items-center mx-2 gap-2 text-gray-600 font-semibold cursor-pointer"
                  onClick={() => {
                    const next = !allVisible;
                    handleLegendGroupClick(
                      items.filter((i) => !i.ignoreGroupSelection).map((i) => i.id),
                      next
                    );
                  }}
                >
                  <input
                    type="checkbox"
                    style={{width: ITEM_SIZE, height: ITEM_SIZE}}
                    readOnly
                    checked={allVisible}
                    ref={(ref) => {
                      if (ref) {
                        ref.indeterminate = !allVisible && someVisible;
                      }
                    }}
                  />

                  <span>{group}</span>
                </div>
                <div className="flex flex-col gap-1">
                  {items.toReversed().map(({id, display, makeIcon, shortDisplayName}) => {
                    const isVisible = isVisibleById[id];
                    return (
                      <div
                        key={id}
                        data-legend-id={id}
                        className={`group flex items-center mx-2 ${
                          !isVisible && 'opacity-50'
                        } cursor-pointer`}
                        onClick={() => {
                          handleLegendItemClick(id, !isVisible);
                        }}
                      >
                        <div className="relative">
                          <input
                            type="checkbox"
                            className={`absolute group-hover:opacity-100 opacity-0 ${!isVisible && 'opacity-100'}`}
                            style={{width: ITEM_SIZE, height: ITEM_SIZE}}
                            readOnly
                            checked={isVisible}
                          />

                          <svg
                            width={ITEM_SIZE}
                            height={ITEM_SIZE}
                            style={{borderRadius: TAB_BORDER_RADIUS}}
                            className={isVisible ? 'opacity-100' : 'opacity-0'}
                          >
                            {makeIcon(-1, 0)}
                          </svg>
                        </div>
                        <div className="ml-2 select-none">{shortDisplayName || display}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>
      {hasExpandedOnlyLegendItems && (
        <div className="mt-2 flex justify-center">
          <button
            type="button"
            className="text-blue-600 hover:text-blue-700 underline"
            onClick={() => {
              setIsExpanded(!isExpanded);
              if (!isExpanded) {
                recordEvent('legend_expanded');
              }
            }}
          >
            {isExpanded ? t('showLess') : t('showMore')}
          </button>
        </div>
      )}
    </div>
  );
}

export function makeLine(lineProps: React.SVGProps<SVGLineElement>): React.ReactElement {
  return (
    <line
      x1={0}
      x2={ITEM_SIZE}
      y1={ITEM_SIZE / 2}
      y2={ITEM_SIZE / 2}
      strokeWidth={2}
      {...lineProps}
    />
  );
}

export function makeRect(rectProps: React.SVGProps<SVGRectElement>): React.ReactElement {
  return (
    <rect
      height={ITEM_SIZE}
      width={ITEM_SIZE}
      rx={TAB_BORDER_RADIUS}
      ry={TAB_BORDER_RADIUS}
      {...rectProps}
    />
  );
}
